import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  AdminAffiliateEarning,
  useVoidEarningMutation,
} from 'store/services/affiliates/affiliatesApi';
import { formatCents, getApiErrorMessage } from './affiliateAdminUtils';

interface VoidEarningModalProps {
  /** The earning to void; modal is closed when null. */
  earning: AdminAffiliateEarning | null;
  onClose: () => void;
}

const VoidEarningModal: React.FC<VoidEarningModalProps> = ({ earning, onClose }) => {
  const [reason, setReason] = useState('');
  const [voidEarning, { isLoading }] = useVoidEarningMutation();

  const isOpen = earning !== null;

  // Reset the reason whenever the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!earning) return null;

  const trimmedReason = reason.trim();
  const isValid = trimmedReason.length > 0;

  const handleConfirm = async () => {
    if (!isValid || isLoading) return;
    try {
      await voidEarning({ id: earning.id, reason: trimmedReason }).unwrap();
      toast.success(
        `Voided earning #${earning.id} (${formatCents(earning.amountCents)}) for ${earning.affiliateUsername}.`
      );
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to void earning. Please try again.'));
    }
  };

  const modalContent = (
    <div className="admin-affil-modal-overlay" onClick={onClose}>
      <div className="admin-affil-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-affil-modal-header">
          <h2 className="admin-affil-modal-title">Void Earning</h2>
          <button className="admin-affil-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="admin-affil-modal-content">
          <p className="admin-affil-modal-text">
            You are voiding a <strong>{formatCents(earning.amountCents)}</strong> earning for
            partner <strong>{earning.affiliateUsername}</strong> (referred user:{' '}
            {earning.referredUsername}).
          </p>

          <div className="admin-affil-form-group">
            <label htmlFor="admin-affil-void-reason" className="admin-affil-form-label">
              Reason for voiding <span className="admin-affil-required">*</span>
            </label>
            <textarea
              id="admin-affil-void-reason"
              className="admin-affil-form-textarea"
              placeholder="Explain why this earning is being voided (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={isLoading}
            />
            {reason.length > 0 && !isValid && (
              <div className="admin-affil-validation-hint">Reason cannot be blank</div>
            )}
          </div>
        </div>

        <div className="admin-affil-modal-footer">
          <MagicButton label="Cancel" color="grey" onPress={onClose} isDisabled={isLoading} />
          <MagicButton
            label={isLoading ? 'Voiding...' : 'Void Earning'}
            color="default"
            onPress={handleConfirm}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VoidEarningModal;
